/*
  pickup-stock-check.js

  Shopify melder en butik som klar til afhentning, også når varen kun kan
  overføres fra en anden butik. Dette script henter det faktiske lagerantal i
  hver butik via Storefront API (quantityAvailable) og retter afhentningsteksten
  og ikonerne, så kun butikker med varen på lager vises som tilgængelige.

  Fejler opslaget (manglende token, produkt ikke i Headless-kanalen, netværk),
  bliver Shopifys standardtekst stående, og årsagen skrives i browserkonsollen.
*/
(() => {
  if (customElements.get('pickup-stock-check')) return;

  const QUERY = `
    query PickupStock($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          storeAvailability(first: 50) {
            nodes {
              quantityAvailable
              location { id }
            }
          }
        }
      }
    }
  `;
  const TIMEOUT_MS = 5000;
  const cache = new Map();

  function fetchStock(variantId, token, apiVersion) {
    if (cache.has(variantId)) return cache.get(variantId);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const request = fetch(`/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: QUERY,
        variables: { id: `gid://shopify/ProductVariant/${variantId}` },
      }),
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((json) => {
        if (json.errors && json.errors.length) {
          throw new Error(json.errors.map((e) => e.message).join('; '));
        }
        const nodes = json.data?.node?.storeAvailability?.nodes;
        if (!nodes) {
          throw new Error('Varianten blev ikke fundet. Er produktet gjort tilgængeligt i Headless-kanalen?');
        }
        const stock = new Map();
        nodes.forEach((node) => {
          stock.set(String(node.location.id).split('/').pop(), Number(node.quantityAvailable) || 0);
        });
        return stock;
      })
      .finally(() => clearTimeout(timer));

    cache.set(variantId, request);
    request.catch(() => cache.delete(variantId));
    return request;
  }

  function joinNames(names) {
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`;
  }

  class PickupStockCheck extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this.run();
    }

    async run() {
      const { variantId, token, apiVersion } = this.dataset;
      const locations = Array.from(this.querySelectorAll('[data-location]'), (el) => ({
        id: el.dataset.id,
        name: el.dataset.name,
        shopifyAvailable: el.dataset.available === 'true',
      }));

      try {
        const stock = await fetchStock(variantId, token, apiVersion);
        locations.forEach((loc) => {
          // Mangler butikken i svaret, bruges Shopifys egen vurdering for den butik.
          loc.inStock = stock.has(loc.id) ? stock.get(loc.id) > 0 : loc.shopifyAvailable;
        });
        this.updatePreview(locations);
        this.updateDrawer(locations);
      } catch (error) {
        console.warn('[pickup-stock-check] Viser Shopifys standardtekst:', error.message);
      } finally {
        this.removeAttribute('data-pending');
      }
    }

    icon(state) {
      const template = this.querySelector(`template[data-icon="${state}"]`);
      const element = template && template.content.firstElementChild;
      return element ? element.cloneNode(true) : null;
    }

    updatePreview(locations) {
      const preview = this.closest('pickup-availability-preview');
      const value = preview && preview.querySelector('[data-pickup-value]');
      if (!value) return;

      const names = locations.filter((loc) => loc.inStock).map((loc) => loc.name);
      preview.dataset.state = names.length ? 'available' : 'unavailable';

      if (names.length) {
        value.textContent = `Tilgængelig i ${joinNames(names)}`;
      } else if (locations.length === 1) {
        value.textContent = `Ikke muligt i ${locations[0].name} lige nu`;
      } else {
        value.textContent = 'Ikke muligt i butikkerne lige nu';
      }
    }

    updateDrawer(locations) {
      const scope = this.closest('pickup-availability') || document;
      locations.forEach((loc) => {
        const selector = `[data-pickup-location-id="${loc.id}"]`;
        let items = scope.querySelectorAll(selector);
        if (!items.length && scope !== document) items = document.querySelectorAll(selector);

        items.forEach((item) => {
          item.dataset.pickupInStock = String(loc.inStock);
          const holder = item.querySelector('p');
          const currentIcon = holder && holder.firstElementChild;
          const icon = this.icon(loc.inStock ? 'available' : 'unavailable');
          if (currentIcon && icon) currentIcon.replaceWith(icon);
        });
      });
    }
  }

  customElements.define('pickup-stock-check', PickupStockCheck);
})();